import { EarlyAccessDialog } from "@/components/EarlyAccessDialog";
import { Link } from "react-router-dom";
import { TMVLogo } from "./TMVLogo";
import { Button } from "./ui/button";
import { NewsletterDialog } from "./NewsletterDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useAdmin";
import { useLanguage } from "@/contexts/LanguageContext";
import { Shield, ChevronDown, Menu } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FlagSE = () => (
  <svg width="20" height="14" viewBox="0 0 20 14">
    <rect width="20" height="14" fill="#006AA7" />
    <rect x="6" y="0" width="2" height="14" fill="#FECC02" />
    <rect x="0" y="6" width="20" height="2" fill="#FECC02" />
  </svg>
);

const FlagGB = () => (
  <svg width="20" height="14" viewBox="0 0 60 30">
    <clipPath id="t"><rect width="60" height="30" /></clipPath>
    <g clipPath="url(#t)">
      <rect width="60" height="30" fill="#00247d" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#cf142b" strokeWidth="4" clipPath="url(#t)" />
      <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 V30 M0,15 H60" stroke="#cf142b" strokeWidth="6" />
    </g>
  </svg>
);

export const Navigation = () => {
  const { user } = useAuth();
  const { data: isAdmin } = useIsAdmin();
  const { language, setLanguage, t } = useLanguage();
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  return (
    <nav className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <NewsletterDialog open={newsletterOpen} onOpenChange={setNewsletterOpen} />

      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <TMVLogo />
          </Link>

          {/* Main Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/how-it-works"
              className="text-foreground/80 hover:text-foreground transition-colors font-medium uppercase"
            >
              {t("nav.howItWorks")}
            </Link>

            <Link
              to="/pricing"
              className="text-foreground/80 hover:text-foreground transition-colors font-medium uppercase"
            >
              Pricing
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger className="text-foreground/80 hover:text-foreground transition-colors font-medium uppercase flex items-center gap-1 outline-none">
                About us
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[14rem]">
                <DropdownMenuItem asChild className="uppercase font-medium cursor-pointer">
                  <Link to="/about">About us</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setNewsletterOpen(true)} className="uppercase font-medium cursor-pointer">
                  {t("nav.joinNewsletter")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {isAdmin && (
              <div className="flex items-center gap-4">
                <Link
                  to="/admin/early-access"
                  className="text-foreground/80 hover:text-foreground transition-colors font-medium uppercase flex items-center gap-1.5"
                >
                  <Shield className="h-4 w-4" />
                  {t("nav.admin")}
                </Link>
                <Link
                  to="/admin/material-requests"
                  className="text-foreground/80 hover:text-foreground transition-colors font-medium uppercase text-sm"
                >
                  Requests
                </Link>
              </div>
            )}
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-3">
            {user && isAdmin && (
              <Link to="/admin/early-access" className="md:hidden">
                <Button variant="outline" size="icon">
                  <Shield className="h-4 w-4" />
                </Button>
              </Link>
            )}

            <EarlyAccessDialog><Button className="uppercase font-medium tracking-normal">Get started</Button></EarlyAccessDialog>
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="md:hidden">
                <Button variant="outline" size="icon" aria-label="Open menu" className="rounded-none">
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[14rem] rounded-none">
                <DropdownMenuItem asChild className="uppercase font-medium cursor-pointer">
                  <Link to="/how-it-works">{t("nav.howItWorks")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="uppercase font-medium cursor-pointer">
                  <Link to="/pricing">Pricing</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="uppercase font-medium cursor-pointer">
                  <Link to="/about">About us</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setNewsletterOpen(true)} className="uppercase font-medium cursor-pointer">
                  {t("nav.joinNewsletter")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};
